import React, { useState, useEffect, useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';

import {
  getTimezoneDate,
  getTimezoneTime,
  setTimezoneDate,
  setTimezoneTime,
} from '../../helpers/timezones';
import UserService from '../../services/UserService';
import AdminService from '../../services/AdminService';
import { UiContext } from '../../context/UiContext';
import { hasAuthData } from '../../helpers/authStorage';
import { startTimeNotInPastIfToday } from '../../helpers/validation';

import FormButton from '../../components/FormButton';
import DatePicker from '../../components/Form/DatePicker';
import TimeRange from '../../components/Form/TimeRange';

import classes from './styles.module.scss';

export default function ClientRescheduleModal({
  show,
  handleClose,
  interview,
  jobApplicationId,
  refetch,
}) {
  const [isTriedToSubmit, setIsTriedToSubmit] = useState(false);

  const { showNotification, setIsFetching, showModal } = useContext(UiContext);

  const { t } = useTranslation();

  const isAuthorized = hasAuthData();

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
    enabled: isAuthorized,
  });

  const validationSchema = useMemo(() => {
    const requiredMsg = t('modals.ScheduleInterviewModal.requiredField');
    return yup.object({
      interviewDate: yup.date().required(requiredMsg),
      startTime: yup
        .string()
        .trim()
        .required(t('modals.SetOnboardingModal.startTimeRequired'))
        .test(
          'greater-than-now',
          t('modals.ScheduleInterviewModal.startTimeGreaterThanCurrentTime'),
          startTimeNotInPastIfToday('interviewDate')
        ),
      endTime: yup
        .string()
        .trim()
        .required(t('modals.ScheduleInterviewModal.endTimeRequired'))
        .test(
          'less-than-start',
          t('modals.ScheduleInterviewModal.endTimeGreaterThanStartTime'),
          (value, ctx) => {
            if (!ctx.parent.startTime) {
              return true;
            }
            const endTime = parseInt(value?.replace(':', ''), 10);
            const startTime = parseInt(
              ctx.parent.startTime?.replace(':', ''),
              10
            );
            return !(endTime <= startTime);
          }
        ),
    });
  }, [t]);

  const initialInterviewDate = useMemo(() => {
    if (!interview?.interviewDate || !me?.userProfile?.timezone) return '';
    return getTimezoneDate(
      interview.interviewDate,
      me.userProfile.timezone,
      interview.startTime
    )[0];
  }, [
    interview?.interviewDate,
    interview?.startTime,
    me?.userProfile?.timezone,
  ]);

  const initialStartTime = useMemo(() => {
    if (!interview?.startTime || !me?.userProfile?.timezone) return '';
    return getTimezoneTime(interview.startTime, me.userProfile.timezone);
  }, [interview?.startTime, me?.userProfile?.timezone]);

  const initialEndTime = useMemo(() => {
    if (!interview?.endTime || !me?.userProfile?.timezone) return '';
    return getTimezoneTime(interview.endTime, me.userProfile.timezone);
  }, [interview?.endTime, me?.userProfile?.timezone]);

  const formik = useFormik({
    initialValues: {
      interviewDate: initialInterviewDate,
      startTime: initialStartTime,
      endTime: initialEndTime,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setIsFetching(true);

        await AdminService.requestClientInterviewReschedule({
          jobApplicationId,
          interviewId: interview?.id,
          suggestedInterviewDate: setTimezoneDate(
            values.interviewDate,
            values.startTime,
            me?.userProfile?.timezone
          ),
          suggestedStartTime: setTimezoneTime(
            values.startTime,
            me?.userProfile?.timezone
          ),
          suggestedEndTime: setTimezoneTime(
            values.endTime,
            me?.userProfile?.timezone
          ),
        });

        handleClose();
        if (refetch) {
          await refetch();
        }
        showNotification({
          text: t('modals.ClientRescheduleModal.changeRequestSent'),
        });
      } catch (error) {
        showModal({
          title: t('modals.ScheduleInterviewModal.error'),
          text:
            error?.response?.data?.message ||
            error?.message ||
            t('modals.ScheduleInterviewModal.errorMessage'),
        });
      } finally {
        setIsFetching(false);
      }
    },
  });

  useEffect(() => {
    return () => {
      formik.resetForm();
      setIsTriedToSubmit(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const isDisabled =
    !formik.values.interviewDate ||
    !formik.values.startTime ||
    !formik.values.endTime;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      enforceFocus={false}
      restoreFocus={false}
      className={classes.ClientRescheduleModal}
      backdropClassName={classes.backdrop}
      contentClassName={classes.modalContent}
      dialogClassName={classes.dialog}
    >
      <div className={classes.content}>
        <header>
          <h1>{t('modals.ClientRescheduleModal.title')}</h1>
          <i className={classes.closeIcon} onClick={handleClose}>
            Close
          </i>
        </header>
        <p className={classes.subtitle}>
          {t('modals.ClientRescheduleModal.subtitle')}
        </p>
        <div className={classes.container}>
          <div className={classes.row}>
            <div className={classes.col}>
              <DatePicker
                dateFieldWidth={320}
                label={t('modals.ClientRescheduleModal.suggestedDate')}
                name="interviewDate"
                value={formik.values.interviewDate}
                setFieldValue={formik.setFieldValue}
                error={formik.errors.interviewDate}
                setFieldTouched={formik.setFieldTouched}
              />
            </div>
            <div className={classes.col}>
              <TimeRange
                fromValue={formik.values.startTime}
                toValue={formik.values.endTime}
                setFieldValue={formik.setFieldValue}
                fromName="startTime"
                toName="endTime"
                error={formik.errors.endTime}
                startTimeError={formik.errors.startTime}
                touched={isTriedToSubmit}
                showDuration
                label={t('modals.ClientRescheduleModal.suggestedTime')}
              />
            </div>
          </div>
        </div>
        <footer>
          <FormButton
            variant="reset"
            style={{ width: 220, height: 52 }}
            onClick={handleClose}
          >
            {t('common.cancel')}
          </FormButton>
          <FormButton
            variant="submit"
            style={{ width: 220, height: 52 }}
            disabled={isDisabled || formik.isSubmitting}
            onClick={() => {
              setIsTriedToSubmit(true);
              formik.handleSubmit();
            }}
          >
            {t('modals.ClientRescheduleModal.sendRequest')}
          </FormButton>
        </footer>
      </div>
    </Modal>
  );
}
